import Vue from 'vue';
import VueCompositionApi, { reactive, watch, ref, Ref, computed } from '@vue/composition-api';

import { Immutable, Stateful } from './common';
import { AudioTrackInfo, AudioTrack, AudioSource } from './storyblok-audio-lib';

Vue.use(VueCompositionApi);

export type AudioPlayerSettings = {
  autoPlayNextTrack: boolean;
  shuffle: boolean;
  loopTrack: boolean;
  loopPlaylist: boolean;
};

export type AudioPlayerState = {
  queue: AudioTrack[];
  currentTrackIdx: number;
  settings: AudioPlayerSettings;
  version: number;
};

export const DefaultAudioPlayerState: AudioPlayerState = {
  queue: [],
  settings: {
    autoPlayNextTrack: true,
    shuffle: false,
    loopTrack: false,
    loopPlaylist: true,
  },
  currentTrackIdx: -1,
  version: Date.now(),
};

export type AudioPlayerModel = Stateful<AudioPlayerState> & {
  type: 'AudioPlayerModel';
  setSetting: <K extends keyof AudioPlayerSettings>(key: K, value: AudioPlayerSettings[K]) => void;
  isPlaying: Immutable<Ref<boolean>>;
  currentAudioTrack: Immutable<Ref<AudioTrack | undefined>>;
  enqueueAudioTrack(src: AudioSource, info: Immutable<AudioTrackInfo[]>): number;
  sortAudioTracks(info: Immutable<AudioTrackInfo[]>): void;
  hasAudioTrack(key: number | AudioSource): boolean;
  getAudioTrackIds(key: AudioSource): number[];
  setCurrentAudioTrack(id: number): void;
  togglePlay(): Promise<void>;
  stop(): void;
  prev(): Promise<Immutable<AudioTrack> | undefined>;
  next(): Promise<Immutable<AudioTrack> | undefined>;

  currentTime: Immutable<Ref<number>>;
  setCurrentTime(value: number): void;
  duration: Immutable<Ref<number>>;
};

export function createAudioPlayerModel(state = DefaultAudioPlayerState): AudioPlayerModel {
  // deep copy trick with json serialization
  const _state: AudioPlayerState = reactive(JSON.parse(JSON.stringify(state)));
  // TODO support SSR
  const _audio = document.createElement('audio');

  let _nextId = _state.queue.reduce((acc, v) => (v.id > acc ? v.id : acc), 0) + 1;

  const isPlaying = ref(false);

  const duration = ref(Number.isNaN(_audio.duration) ? 0 : _audio.duration);

  const currentTime = ref(0);

  function setCurrentTime(value: number) {
    _audio.currentTime = value;
    // ontimeupdate is not called immediately, this avoids glitches
    currentTime.value = value;
  }

  function setSetting<K extends keyof AudioPlayerSettings>(key: K, value: AudioPlayerSettings[K]) {
    _state.settings[key] = value;
  }

  const currentAudioTrack = computed(() => {
    if (_state.currentTrackIdx < 0) return undefined;
    return _state.queue[_state.currentTrackIdx];
  });

  async function updateAudioSource() {
    await new Promise((r) => {
      const src = currentAudioTrack.value?.src;
      if (src !== undefined) {
        if (typeof src === 'string') _audio.src = src;
        else {
          _audio.textContent = ''; // removes source elments
          src.forEach((s) => {
            const source = document.createElement('source');
            source.src = s;
            _audio.append(source);
          });
        }
        setCurrentTime(0);
        _audio.load();
        if (isPlaying.value) {
          // const isFirefox = navigator.userAgent.includes('Firefox');

          const callback = async () => {
            try {
              await _audio.play();
              /*if (isFirefox) */_audio.oncanplay = null;
              // else _audio.oncanplaythrough = null;
            } catch (e) {
              console.error(e);
            } finally {
              r();
            }
          };
          if (_audio.readyState >= 3) {
            callback();
          } else /*if (isFirefox)*/ _audio.oncanplay = callback;
          // else _audio.oncanplaythrough = callback;
        } else {
          r();
        }
      }
    });
  }

  function hasAudioTrack(key: number | AudioSource) {
    return (
      _state.queue.filter((a) => {
        if (typeof key === 'number') return a.id === key;
        if (typeof key === 'string') return a.src === key;
        return JSON.stringify(key) === JSON.stringify(a.src);
      }).length > 0
    );
  }

  function getAudioTrackIds(key: AudioSource) {
    return _state.queue.filter((at) => at.src === key).map((at) => at.id);
  }

  async function setCurrentAudioTrack(id: number) {
    const idx = _state.queue.findIndex((at) => at.id === id);
    if (idx !== -1) {
      _state.currentTrackIdx = idx;
      await updateAudioSource();
    }
  }

  // TODO pretty inefficient, but is called very rarely...
  function sortAudioTracks(info: Immutable<AudioTrackInfo[]>) {
    _state.queue.sort((a, b) => {
      const ai = info.find((i) => a.src === i.newAudioFile?.audio || a.src === i.audioFile?.filename);
      const bi = info.find((i) => b.src === i.newAudioFile?.audio || b.src === i.audioFile?.filename);

      if (!ai && !bi ) return 0;
      if (!ai) return 1;
      if (!bi ) return -1;
      if (ai.artist === bi.artist)
      {
        if(ai.cover.filename === bi.cover.filename) return 0;
        return (ai.cover.filename < bi.cover.filename) ? -1 : 1;
      }
      return ai.artist < bi.artist ? -1 : 1;
    });
  }

  function enqueueAudioTrack(src: AudioSource, info: Immutable<AudioTrackInfo[]>) {
    const id = _nextId;
    _state.queue.push({ src, id });
    sortAudioTracks(info);
    _nextId += 1;
    return id;
  }

  async function nextOrPrev(n: boolean) {
    if (_state.queue.length === 0) return undefined;

    const end = _state.queue.length - 1;
    if (_state.settings.shuffle) {
      let newIdx = 0;
      if (_state.queue.length > 1) {
        do {
          newIdx = Math.floor(_state.queue.length * Math.random());
        } while (_state.currentTrackIdx === newIdx);
      }
      _state.currentTrackIdx = newIdx;
    } else if (_state.currentTrackIdx === (n ? end : 0)) {
      const trackIdx = !n ? end : 0;
      _state.currentTrackIdx = _state.settings.loopPlaylist ? trackIdx : -1;
    } else if (_state.currentTrackIdx === undefined) {
      _state.currentTrackIdx = 0;
    } else {
      _state.currentTrackIdx += n ? 1 : -1;
    }
    if (_state.currentTrackIdx === undefined) {
      isPlaying.value = false;
      _audio.src = '';
      _audio.textContent = ''; // removes source elments
      return undefined;
    }
    await updateAudioSource();
    return _state.queue[_state.currentTrackIdx];
  }

  async function next() {
    return nextOrPrev(true);
  }
  async function prev() {
    return nextOrPrev(false);
  }

  function _stop() {
    isPlaying.value = false;
  }

  function stop() {
    _audio.pause();
    _audio.currentTime = 0;
    _stop();
  }

  async function togglePlay() {
    try {
      if (currentAudioTrack.value === undefined) {
        await next();
      }
      if (isPlaying.value) {
        _audio.pause();
        isPlaying.value = false;
      } else {
        await _audio.play();
      }
    } catch (e) {
      console.error(e);
    }
  }

  updateAudioSource();

  _audio.onplay = () => {
    if (currentAudioTrack.value !== undefined) isPlaying.value = true;
  };

  _audio.onended = async () => {
    if (_state.settings.autoPlayNextTrack) await next();
    else isPlaying.value = false;
  };
  _audio.ondurationchange = () => {
    duration.value = _audio.duration;
  };

  _audio.ontimeupdate = () => {
    currentTime.value = _audio.currentTime;
  };
  _audio.onerror = _stop;
  // don't 'stop' the audio with these events, because it produces glitches, when the next or previous button is pressed
  // (switches between play and pause for a short period, although this is technically correct)
  // _audio.onemptied = _stop;
  // _audio.onabort = _stop;
  // _audio.onpause = _stop;

  watch(
    () => _state.settings.loopTrack,
    () => {
      _audio.loop = _state.settings.loopTrack;
    }
  );

  // new version everytime the state changes
  // TODO might result in endless circular recursion
  // watch(
  //   () => _state.filter(), // programmatic approach to filter all keys but 'version'
  //   () => {_state.version = Date.now()}
  // );

  return {
    type: 'AudioPlayerModel',
    state: _state,
    isPlaying,
    setSetting,
    hasAudioTrack,
    getAudioTrackIds,
    currentAudioTrack,
    setCurrentAudioTrack,
    sortAudioTracks,
    enqueueAudioTrack,
    togglePlay,
    stop,
    prev,
    next,
    duration,
    currentTime,
    setCurrentTime,
  };
}
